// src/components/CapoOfficina/CapoOfficinaWorkHours.jsx
// Gestione ore per Capo Officina e Meccanici
import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { collection, getDocs, getDoc, doc, query, where } from 'firebase/firestore';
import { getDayInfo, getMonthHolidays } from '../../utils/holidaysUtils';
import { applyManualWorkHoursChanges, diffEditedEntries, snapshotEntries } from '../../services/workHoursAudit';
import './CapoOfficinaWorkHours.css';

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  return [currentYear, currentYear - 1, currentYear - 2].sort((a, b) => b - a);
};

const CapoOfficinaWorkHours = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [timeEntries, setTimeEntries] = useState([]);
  // Valori caricati dal database (per salvare solo i giorni modificati) e ricarica dopo il salvataggio
  const [loadedSnapshot, setLoadedSnapshot] = useState({ key: '', entries: [] });
  const [reloadKey, setReloadKey] = useState(0);
  const [monthHolidays, setMonthHolidays] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  // Stato per l'autocompletamento
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedEmployeeData, setSelectedEmployeeData] = useState(null);
  
  // Stato per il capo officina corrente
  const [currentCapoOfficina, setCurrentCapoOfficina] = useState(null);
 
  // Carica info capo officina corrente
  useEffect(() => {
    const getCurrentCapoOfficina = async () => {
      if (auth.currentUser) {
        try {
          const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const capoOfficinaName = userData.nome && userData.cognome 
              ? `${userData.nome} ${userData.cognome}`
              : userData.email;
            setCurrentCapoOfficina({
              uid: auth.currentUser.uid,
              name: capoOfficinaName,
              email: userData.email
            });
            console.log('Capo Officina caricato:', capoOfficinaName);
          }
        } catch (error) {
          console.error("Errore nel caricamento dati capo officina:", error);
        }
      }
    };
    
    getCurrentCapoOfficina();
  }, []);

  // Carica solo meccanici e il capo officina stesso
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const usersRef = collection(db, "users");
        
        // Query per meccanici
        const meccaniciQuery = query(usersRef, where("incarico", "==", "Meccanico"));
        const meccaniciSnapshot = await getDocs(meccaniciQuery);
        
        const empList = meccaniciSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            nome: data.nome || '',
            cognome: data.cognome || '',
            email: data.email || '',
            incarico: data.incarico || 'N/A',
            displayName: data.nome && data.cognome
              ? `${data.nome} ${data.cognome}`
              : data.email
          };
        });

        // Aggiungi il capo officina stesso
        if (currentCapoOfficina) {
          const userDoc = await getDoc(doc(db, "users", currentCapoOfficina.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            empList.unshift({
              id: currentCapoOfficina.uid,
              nome: userData.nome || '',
              cognome: userData.cognome || '',
              email: userData.email || '',
              incarico: userData.incarico || 'Capo Officina',
              displayName: currentCapoOfficina.name
            });
          }
        }

        // Ordina per nome
        empList.sort((a, b) => a.displayName.localeCompare(b.displayName));
        
        setEmployees(empList);
        setFilteredEmployees(empList);
        console.log(`Caricati ${empList.length} dipendenti (meccanici + capo officina)`);
      } catch (error) {
        console.error("Errore nel caricamento dei dipendenti:", error);
        showNotification("Errore nel caricamento dei dipendenti", "error");
      }
    };

    if (currentCapoOfficina) {
      fetchEmployees();
    }
  }, [currentCapoOfficina]);

  // Filtra dipendenti in base al termine di ricerca
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredEmployees(employees);
      setShowSuggestions(false);
    } else {
      const filtered = employees.filter(emp =>
        emp.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEmployees(filtered);
      setShowSuggestions(filtered.length > 0);
    }
  }, [searchTerm, employees]);

  // Gestisce la selezione del dipendente dall'autocomplete
  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee.id);
    setSelectedEmployeeData(employee);
    setSearchTerm(employee.displayName);
    setShowSuggestions(false);
  };

 // Genera tutti i giorni del mese con info festività - VERSIONE CORRETTA
const generateCompleteMonthDays = (month, year) => {
    const monthInt = parseInt(month);
    const yearInt = parseInt(year);
    const daysInMonth = new Date(yearInt, monthInt, 0).getDate();
    const days = [];
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = `${yearInt}-${monthInt.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      
      // Ottieni informazioni sul giorno
      const dayInfo = getDayInfo(dateString);
      
      // ✅ CORREZIONE: Gestisci il caso in cui getDayInfo restituisce null
      if (!dayInfo) {
        console.warn(`⚠️ getDayInfo ha restituito null per ${dateString} - uso fallback`);
        
        // Calcola manualmente il giorno della settimana come fallback sicuro
        const dateForDayName = new Date(yearInt, monthInt - 1, day);
        const dayOfWeekName = dateForDayName.toLocaleDateString('it-IT', { weekday: 'long' });
        const dayOfWeek = dateForDayName.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        
        days.push({
          date: dateString,
          day: day,
          dayOfWeek: dayOfWeekName,
          total: '',
          overtime: '',
          notes: '',
          isWeekend: isWeekend,
          isHoliday: false,
          holidayName: null,
          hasData: false
        });
        continue; // Passa al prossimo giorno
      }
      
      // Calcola il giorno della settimana per il nome in italiano
      const dateForDayName = new Date(yearInt, monthInt - 1, day);
      const dayOfWeekName = dateForDayName.toLocaleDateString('it-IT', { weekday: 'long' });
      
      days.push({
        date: dateString,
        day: day,
        dayOfWeek: dayOfWeekName,
        total: '',
        overtime: '',
        notes: '',
        isWeekend: dayInfo.isWeekend || false,
        isHoliday: dayInfo.isHoliday || false,
        holidayName: dayInfo.holidayName || null,
        hasData: false
      });
    }
    
    return days;
  };

  // Mostra notifica
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 4000);
  };

  // Carica festività quando cambia mese/anno
  useEffect(() => {
    if (selectedMonth && selectedYear) {
      const monthInt = parseInt(selectedMonth);
      const yearInt = parseInt(selectedYear);
      const holidays = getMonthHolidays(monthInt, yearInt);
      setMonthHolidays(holidays);
    }
  }, [selectedMonth, selectedYear]);

  // Carica ore lavorative
  useEffect(() => {
    if (!selectedEmployee || !selectedMonth || !selectedYear) {
      setTimeEntries([]);
      return;
    }

    // Mostra i dati caricati e ne conserva una fotografia: al salvataggio
    // vengono scritti solo i giorni che differiscono da questa
    const loadedKey = `${selectedEmployee}_${selectedMonth}_${selectedYear}`;
    const showLoadedEntries = (entries) => {
      setTimeEntries(entries);
      setLoadedSnapshot({ key: loadedKey, entries: snapshotEntries(entries) });
    };

    const fetchTimeEntries = async () => {
      try {
        setIsLoading(true);
        const workHoursRef = doc(db, "workHours", `${selectedEmployee}_${selectedMonth}_${selectedYear}`);
        const workHoursDoc = await getDoc(workHoursRef);
        
        const completeMonthData = generateCompleteMonthDays(selectedMonth, selectedYear);
        
        if (workHoursDoc.exists()) {
          const data = workHoursDoc.data();
          
          if (data.entries && Array.isArray(data.entries)) {
            const existingDataMap = {};
            data.entries.forEach(entry => {
              if (entry.date) {
                existingDataMap[entry.date] = {
                  total: entry.total || '',
                  overtime: entry.overtime || '',
                  notes: entry.notes || '',
                  hasData: true,
                  // Mantieni metadata di tracciamento se esistono
                  lastModified: entry.lastModified,
                  modifiedBy: entry.modifiedBy,
                  modifierName: entry.modifierName,
                  modifications: entry.modifications
                };
              }
            });
            
            const mergedData = completeMonthData.map(dayEntry => {
              const existingEntry = existingDataMap[dayEntry.date];
              if (existingEntry) {
                return {
                  ...dayEntry,
                  ...existingEntry
                };
              }
              return dayEntry;
            });
            
            showLoadedEntries(mergedData);
          } else {
            showLoadedEntries(completeMonthData);
          }
        } else {
          showLoadedEntries(completeMonthData);
        }
      } catch (error) {
        console.error("Errore nel caricamento delle ore lavorative:", error);
        showNotification("Errore nel caricamento delle ore lavorative", "error");
        
        const completeMonthData = generateCompleteMonthDays(selectedMonth, selectedYear);
        showLoadedEntries(completeMonthData);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTimeEntries();
  }, [selectedEmployee, selectedMonth, selectedYear, reloadKey]);

  // Gestisce il cambio di mese
  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  // Gestisce il cambio di anno
  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  // Gestisce l'input delle ore totali con tracciamento
  const handleTotalHoursChange = (index, value) => {
    let processedValue = value;
    
    if (value === "C" || value === "c") {
      processedValue = "C";
    } else if (value === "M" || value === "m") {
      processedValue = "M";
    } else if (value === "P" || value === "p") {
      processedValue = "P";
    } else if (value === "F" || value === "f") {
      processedValue = "F";
    } else if (value === "A" || value === "a") {
      processedValue = "A";
    }
    
    const newEntries = [...timeEntries];
    const originalValue = newEntries[index].total;
    
    newEntries[index].total = processedValue;
    newEntries[index].hasData = true;
    
    // Se il valore è cambiato, aggiungi nota di modifica
    if (originalValue !== processedValue && currentCapoOfficina) {
      const currentNote = newEntries[index].notes || '';
      const modificaNote = `(modificato da ${currentCapoOfficina.name})`;
      
      // Rimuovi note di modifica precedenti per evitare duplicati
      const cleanNote = currentNote.replace(/\(modificato da [^)]+\)/g, '').trim();
      
      if (processedValue !== '') {
        newEntries[index].notes = cleanNote 
          ? `${cleanNote} ${modificaNote}`
          : modificaNote;
      } else {
        newEntries[index].notes = cleanNote;
      }
    }
    
    if (["M", "P", "F", "A", "C"].includes(processedValue)) {
      newEntries[index].overtime = '';
    }
    
    setTimeEntries(newEntries);
  };

  // Gestisce l'input delle ore di straordinario con tracciamento
  const handleOvertimeChange = (index, value) => {
    const newEntries = [...timeEntries];
    const originalValue = newEntries[index].overtime;
    
    newEntries[index].overtime = value;
    newEntries[index].hasData = true;
    
    // Se il valore è cambiato, aggiungi nota di modifica
    if (originalValue !== value && currentCapoOfficina) {
      const currentNote = newEntries[index].notes || '';
      const modificaNote = `(modificato da ${currentCapoOfficina.name})`;
      
      // Rimuovi note di modifica precedenti per evitare duplicati
      const cleanNote = currentNote.replace(/\(modificato da [^)]+\)/g, '').trim();
      
      if (value !== '' && value !== '0') {
        newEntries[index].notes = cleanNote 
          ? `${cleanNote} ${modificaNote}`
          : modificaNote;
      } else if (originalValue !== '' && originalValue !== '0') {
        newEntries[index].notes = cleanNote 
          ? `${cleanNote} ${modificaNote}`
          : modificaNote;
      }
    }
    
    setTimeEntries(newEntries);
  };

  // Gestisce l'input delle note
  const handleNotesChange = (index, value) => {
    const newEntries = [...timeEntries];
    newEntries[index].notes = value;
    newEntries[index].hasData = true;
    setTimeEntries(newEntries);
  };

  // Salva le modifiche
  const handleSave = async () => {
    if (!selectedEmployee || !selectedMonth || !selectedYear) {
      showNotification("Seleziona dipendente, mese e anno", "error");
      return;
    }
    
    try {
      setIsSaving(true);
      
      // I dati a video devono essere quelli del dipendente/mese selezionato
      if (isLoading || loadedSnapshot.key !== `${selectedEmployee}_${selectedMonth}_${selectedYear}`) {
        showNotification("Attendi il caricamento delle ore prima di salvare", "error");
        return;
      }

      // Si salvano solo i giorni modificati a video, in transazione sopra lo
      // stato attuale del database: niente riscrittura del mese intero e niente
      // giorni vuoti. Ogni modifica viene annotata con autore, data e valori
      // prima → dopo (vedi services/workHoursAudit.js).
      const changes = diffEditedEntries(loadedSnapshot.entries, timeEntries).map(entry => ({
        date: entry.date,
        day: entry.day,
        total: entry.total,
        overtime: entry.overtime,
        notes: entry.notes,
        isWeekend: entry.isWeekend
      }));

      if (changes.length === 0) {
        showNotification("Nessuna modifica da salvare", "success");
        return;
      }

      const result = await applyManualWorkHoursChanges({
        userId: selectedEmployee,
        month: selectedMonth,
        year: selectedYear,
        changes,
        source: 'pannello-capo-officina',
        editor: { id: currentCapoOfficina?.uid, name: currentCapoOfficina?.name, role: 'capo_officina' }
      });

      // Ricarica dal database per mostrare le note di modifica appena scritte
      setReloadKey(key => key + 1);

      showNotification(`Ore lavorative salvate: ${result.changedDays} ${result.changedDays === 1 ? 'giorno modificato' : 'giorni modificati'}`, "success");
      
    } catch (error) {
      console.error("Errore nel salvataggio:", error);
      showNotification("Errore nel salvataggio delle ore", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Calcola totali
  const calculateTotals = () => {
    let totalStandard = 0;
    let totalOvertime = 0;
    let totalDaysWorked = 0;

    timeEntries.forEach(entry => {
      if (entry.hasData && entry.total) {
        if (!isNaN(parseFloat(entry.total))) {
          totalStandard += parseFloat(entry.total);
          totalDaysWorked++;
        }
      }
      
      if (entry.overtime && !isNaN(parseFloat(entry.overtime))) {
        totalOvertime += parseFloat(entry.overtime);
      }
    });

    return { totalStandard, totalOvertime, totalDaysWorked };
  };

  const totals = calculateTotals();

  return (
    <div className="work-hours-container">
      <div className="work-hours-header">
        <h2>📊 Gestione Ore Lavorative</h2>
        <p style={{ color: '#6c757d', marginTop: '8px' }}>
          Gestisci le ore per i meccanici e per te stesso
        </p>
      </div>

      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="filters-container">
        {/* Autocomplete per dipendente */}
        <div className="filter-group">
          <label>Dipendente</label>
          <div className="autocomplete-wrapper">
            <input
              type="text"
              placeholder="Cerca dipendente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setShowSuggestions(filteredEmployees.length > 0)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className="autocomplete-input"
            />
            {showSuggestions && (
              <div className="autocomplete-suggestions">
                {filteredEmployees.map(emp => (
                  <div
                    key={emp.id}
                    className="autocomplete-suggestion"
                    onClick={() => handleEmployeeSelect(emp)}
                  >
                    <div className="suggestion-name">{emp.displayName}</div>
                    <div className="suggestion-detail">{emp.incarico}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mese */}
        <div className="filter-group">
          <label>Mese</label>
          <select value={selectedMonth} onChange={handleMonthChange}>
            <option value="">Seleziona mese</option>
            <option value="1">Gennaio</option>
            <option value="2">Febbraio</option>
            <option value="3">Marzo</option>
            <option value="4">Aprile</option>
            <option value="5">Maggio</option>
            <option value="6">Giugno</option>
            <option value="7">Luglio</option>
            <option value="8">Agosto</option>
            <option value="9">Settembre</option>
            <option value="10">Ottobre</option>
            <option value="11">Novembre</option>
            <option value="12">Dicembre</option>
          </select>
        </div>

        {/* Anno */}
        <div className="filter-group">
          <label>Anno</label>
          <select value={selectedYear} onChange={handleYearChange}>
            {generateYearOptions().map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {/* Pulsante salva */}
        <div className="filter-group">
          <label>&nbsp;</label>
          <button
            onClick={handleSave}
            disabled={!selectedEmployee || !selectedMonth || !selectedYear || isSaving}
            className="btn-save"
          >
            {isSaving ? '💾 Salvataggio...' : '💾 Salva Modifiche'}
          </button>
        </div>
      </div>

      {/* Info dipendente selezionato */}
      {selectedEmployeeData && (
        <div className="selected-employee-info">
          <strong>Dipendente:</strong> {selectedEmployeeData.displayName} 
          <span className="badge">{selectedEmployeeData.incarico}</span>
        </div>
      )}

      {/* Legenda */}
      <div className="legend-container">
        <h4>Legenda</h4>
        <div className="legend-items">
          <span className="legend-item"><strong>M</strong> = Malattia</span>
          <span className="legend-item"><strong>F</strong> = Ferie</span>
          <span className="legend-item"><strong>P</strong> = Permesso</span>
          <span className="legend-item"><strong>C</strong> = Cassa Integrazione</span>
          <span className="legend-item"><strong>A</strong> = Assenza</span>
        </div>
      </div>

      {/* Tabella ore */}
      {isLoading ? (
        <div className="loading-state">⏳ Caricamento dati...</div>
      ) : timeEntries.length === 0 ? (
        <div className="empty-state">
          ℹ️ Seleziona dipendente, mese e anno per visualizzare le ore
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="work-hours-table">
              <thead>
                <tr>
                  <th>Giorno</th>
                  <th>Data</th>
                  <th>Ore Std</th>
                  <th>Straord.</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {timeEntries.map((entry, index) => (
                  <tr
                    key={entry.date}
                    className={`
                      ${entry.isWeekend ? 'weekend-row' : ''}
                      ${entry.isHoliday ? 'holiday-row' : ''}
                    `}
                  >
                    <td>
                      {entry.dayOfWeek}
                      {entry.isHoliday && (
                        <div className="holiday-label">{entry.holidayName}</div>
                      )}
                    </td>
                    <td>{entry.day}</td>
                    <td>
                      <input
                        type="text"
                        value={entry.total}
                        onChange={(e) => handleTotalHoursChange(index, e.target.value)}
                        className="hours-input"
                        placeholder="-"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={entry.overtime}
                        onChange={(e) => handleOvertimeChange(index, e.target.value)}
                        className="hours-input"
                        placeholder="-"
                        step="0.5"
                        disabled={["M", "P", "F", "A", "C"].includes(entry.total)}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={entry.notes}
                        onChange={(e) => handleNotesChange(index, e.target.value)}
                        className="notes-input"
                        placeholder="Note..."
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="totals-row">
                  <td colSpan="2"><strong>TOTALI</strong></td>
                  <td><strong>{totals.totalStandard.toFixed(1)} ore</strong></td>
                  <td><strong>{totals.totalOvertime.toFixed(1)} ore</strong></td>
                  <td><strong>{totals.totalDaysWorked} giorni</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="summary-info">
            <div className="summary-card">
              <div className="summary-label">Ore Standard</div>
              <div className="summary-value">{totals.totalStandard.toFixed(1)}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Straordinari</div>
              <div className="summary-value">{totals.totalOvertime.toFixed(1)}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Giorni Lavorati</div>
              <div className="summary-value">{totals.totalDaysWorked}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Totale Ore</div>
              <div className="summary-value">
                {(totals.totalStandard + totals.totalOvertime).toFixed(1)}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CapoOfficinaWorkHours;